import { getChainById, getTokenPrice, stringToBlobUrl } from "common";
import { useState, useRef, useEffect, useMemo } from "react";
import { useDonateToGitcoin, GITCOIN_RECIPIENT_CONFIG } from "../../DonateToGitcoinContext";
import { DonationInput } from "./DonationInput";
import React from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useCartStorage } from "../../../../store";
import { parseUnits } from "viem";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const DonateToGitcoinContent = /*#__PURE__*/React.memo(_ref => {
  var _tokenAmountInfo$toke;
  let {
    totalAmount,
    totalDonationsByChain
  } = _ref;
  const {
    isEnabled,
    selectedChainId,
    amount,
    selectedChain,
    chains,
    tokenBalances,
    tokenFilters,
    setSelectedChainId,
    setSelectedToken,
    setAmountInWei
  } = useDonateToGitcoin();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    votingTokens,
    tokenAmountInfo
  } = useMemo(() => {
    if (!tokenFilters) {
      return {
        votingTokens: {},
        tokenAmountInfo: null
      };
    }
    const votingTokens = tokenFilters.reduce((acc, _ref2) => {
      var _getChainById;
      let {
        chainId
      } = _ref2;
      const votingToken = useCartStorage.getState().getVotingTokenForChain(chainId);
      const token = (_getChainById = getChainById(chainId)) === null || _getChainById === void 0 ? void 0 : _getChainById.tokens.find(t => t.address.toLowerCase() === votingToken.address.toLowerCase());
      return {
        ...acc,
        [chainId]: {
          address: votingToken.address,
          token
        }
      };
    }, {});
    const tokenAmountInfo = selectedChainId ? {
      token: votingTokens[selectedChainId].token
    } : null;
    return {
      votingTokens,
      tokenAmountInfo
    };
  }, [tokenFilters, selectedChainId]);
  const [tokenAmount, setTokenAmount] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter chains to only show Gitcoin supported ones
  const supportedChains = useMemo(() => chains.filter(chain => chain.id in GITCOIN_RECIPIENT_CONFIG), [chains]);

  // Handle initial setup
  useEffect(() => {
    if (supportedChains.length === 1 && !selectedChainId) {
      setSelectedChainId(supportedChains[0].id);
    }
    setIsInitialized(true);
  }, [supportedChains, selectedChainId, setSelectedChainId]);
  useEffect(() => {
    let isMounted = true;
    const updateTokenAmount = async () => {
      try {
        var _votingTokens$selecte, _tokenBalances$select;
        setIsLoading(true);
        if (!selectedChainId || !amount) {
          setAmountInWei(0n);
          setTokenAmount(0);
          setError(!selectedChainId ? "Please select a chain" : !amount ? "Please enter an amount" : amount === "0" ? "Amount must be greater than 0" : null);
          return;
        }
        const votingToken = (_votingTokens$selecte = votingTokens[selectedChainId]) === null || _votingTokens$selecte === void 0 ? void 0 : _votingTokens$selecte.token;
        if (!votingToken) {
          setError("Selected token not found");
          setTokenAmount(0);
          setAmountInWei(0n);
          return;
        }
        setSelectedToken(votingToken.address);
        const price = await getTokenPrice(votingToken.redstoneTokenId, votingToken.priceSource);
        if (!isMounted) return;
        if (!price || price <= 0) {
          setError("Unable to fetch token price");
          setTokenAmount(0);
          setAmountInWei(0n);
          return;
        }
        const calculatedAmount = Number(amount) === 0 ? 0 : Number(amount) / Number(price);
        const balance = Number(((_tokenBalances$select = tokenBalances[selectedChainId]) === null || _tokenBalances$select === void 0 || (_tokenBalances$select = _tokenBalances$select[votingToken.address]) === null || _tokenBalances$select === void 0 ? void 0 : _tokenBalances$select.toFixed(5)) || 0);
        const existingDonations = totalDonationsByChain[selectedChainId] || 0;
        const totalRequiredAmount = calculatedAmount + existingDonations;
        if (calculatedAmount === 0) {
          setError("Amount must be greater than 0");
          setTokenAmount(0);
          setAmountInWei(0n);
          return;
        }
        if (totalRequiredAmount > balance) {
          setError(`Insufficient balance for total donations on this chain`);
          setTokenAmount(calculatedAmount);
          setAmountInWei(0n);
          return;
        }
        if (isMounted) {
          setError(null);
          setTokenAmount(calculatedAmount);
          setAmountInWei(parseUnits(String(calculatedAmount), votingToken.decimals));
        }
      } catch (err) {
        console.error("Error in token amount calculation:", err);
        if (isMounted) {
          setError("Error calculating token amount");
          setTokenAmount(0);
          setAmountInWei(0n);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    updateTokenAmount();
    return () => {
      isMounted = false;
    };
  }, [selectedChainId, amount, votingTokens, setSelectedToken, setAmountInWei, tokenBalances, totalDonationsByChain]);
  if (!isEnabled || !isInitialized) return null;
  const renderChainOption = chain => {
    var _tokenBalances$chain$, _votingInfo$token;
    const votingInfo = votingTokens[chain.id];
    const balance = (_tokenBalances$chain$ = tokenBalances[chain.id]) === null || _tokenBalances$chain$ === void 0 || (_tokenBalances$chain$ = _tokenBalances$chain$[(votingInfo === null || votingInfo === void 0 ? void 0 : votingInfo.address) || ""]) === null || _tokenBalances$chain$ === void 0 ? void 0 : _tokenBalances$chain$.toFixed(5);
    return /*#__PURE__*/_jsxs("div", {
      className: "flex items-center justify-between w-full",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-2",
        children: [/*#__PURE__*/_jsx("img", {
          className: "w-5 h-5",
          alt: chain.prettyName,
          src: stringToBlobUrl(chain.icon)
        }), /*#__PURE__*/_jsx("span", {
          className: "text-[12px]",
          children: chain.prettyName
        })]
      }), /*#__PURE__*/_jsxs("span", {
        className: "text-[11px] text-gray-500",
        children: ["Balance: ", balance, " ", votingInfo === null || votingInfo === void 0 || (_votingInfo$token = votingInfo.token) === null || _votingInfo$token === void 0 ? void 0 : _votingInfo$token.code]
      })]
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "flex flex-col gap-[9px] w-full",
    children: [/*#__PURE__*/_jsx(DonationInput, {
      totalAmount: Number(totalAmount).toFixed(2)
    }), /*#__PURE__*/_jsxs("div", {
      className: "flex flex-col gap-[6px]",
      children: [/*#__PURE__*/_jsx("span", {
        className: "font-inter text-[12px] font-medium text-foreground",
        children: "Add to listed transaction"
      }), /*#__PURE__*/_jsx("div", {
        className: "relative flex items-center rounded-lg flex-grow w-full",
        children: supportedChains.length === 1 ? /*#__PURE__*/_jsx("div", {
          className: "w-full p-[9px] rounded-[6px] border-[0.75px] border-[#D7D7D7] bg-white font-modern-era font-medium",
          children: renderChainOption(supportedChains[0])
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("div", {
            ref: dropdownRef,
            onClick: () => setIsOpen(!isOpen),
            className: "w-full p-[9px] cursor-pointer rounded-[6px] border-[0.75px] border-[#D7D7D7] bg-white font-modern-era font-medium",
            children: selectedChain ? /*#__PURE__*/_jsxs("div", {
              className: "flex items-center justify-between w-full",
              children: [renderChainOption(selectedChain), /*#__PURE__*/_jsx(ChevronDownIcon, {
                className: "w-5 h-5 ml-2"
              })]
            }) : /*#__PURE__*/_jsx("span", {
              className: "text-[12px]",
              children: "Select chain"
            })
          }), isOpen && /*#__PURE__*/_jsx("div", {
            className: "absolute top-full left-0 right-0 mt-1 bg-white border border-[#D7D7D7] rounded-[6px] shadow-lg z-10",
            children: supportedChains.sort((a, b) => a.prettyName.localeCompare(b.prettyName)).map(chain => /*#__PURE__*/_jsx("div", {
              onClick: () => {
                setSelectedChainId(chain.id);
                setIsOpen(false);
              },
              className: "p-[9px] hover:bg-gray-50 cursor-pointer",
              children: renderChainOption(chain)
            }, chain.id))
          })]
        })
      }), selectedChain && tokenAmountInfo && /*#__PURE__*/_jsxs("div", {
        className: "flex justify-between items-center font-inter text-[11px] font-medium text-foreground",
        children: [/*#__PURE__*/_jsx("span", {
          children: "Donation total"
        }), /*#__PURE__*/_jsx("span", {
          children: isLoading ? "Calculating..." : `${tokenAmount.toFixed(5)} ${(_tokenAmountInfo$toke = tokenAmountInfo.token) === null || _tokenAmountInfo$toke === void 0 ? void 0 : _tokenAmountInfo$toke.code}`
        })]
      })]
    }), isInitialized && error && /*#__PURE__*/_jsx("p", {
      className: "text-[11px] text-red-500",
      children: error
    })]
  });
});